import type {Language, LocalizedString} from '@cohort/shared/schema/common';
import {DatetimeSchema} from '@cohort/shared/schema/common';
import {z} from 'zod';

export const StravaTriggerIntegrationActivityGroups = [
  'RUNNING',
  'CYCLING',
  'WALKING',
  'SWIMMING',
] as const;
export type StravaTriggerIntegrationActivityGroupsType =
  (typeof StravaTriggerIntegrationActivityGroups)[number];

export const StravaActivityTypes = [
  'AlpineSki',
  'BackcountrySki',
  'Badminton',
  'Canoeing',
  'Crossfit',
  'EBikeRide',
  'Elliptical',
  'EMountainBikeRide',
  'Golf',
  'GravelRide',
  'Handcycle',
  'HighIntensityIntervalTraining',
  'Hike',
  'IceSkate',
  'InlineSkate',
  'Kayaking',
  'Kitesurf',
  'MountainBikeRide',
  'NordicSki',
  'Pickleball',
  'Pilates',
  'Racquetball',
  'Ride',
  'RockClimbing',
  'RollerSki',
  'Rowing',
  'Run',
  'Sail',
  'Skateboard',
  'Snowboard',
  'Snowshoe',
  'Soccer',
  'Squash',
  'StairStepper',
  'StandUpPaddling',
  'Surfing',
  'Swim',
  'TableTennis',
  'Tennis',
  'TrailRun',
  'Velomobile',
  'VirtualRide',
  'VirtualRow',
  'VirtualRun',
  'Walk',
  'WeightTraining',
  'Wheelchair',
  'Windsurf',
  'Workout',
  'Yoga',
] as const;
type StravaTriggerIntegrationAvailableActivityType = (typeof StravaActivityTypes)[number];

export const StravaTriggerIntegrationActivityTypesMappedByGroups: Record<
  StravaTriggerIntegrationActivityGroupsType,
  StravaTriggerIntegrationAvailableActivityType[]
> = {
  RUNNING: ['Run', 'TrailRun', 'VirtualRun', 'Wheelchair'],
  CYCLING: [
    'EBikeRide',
    'Elliptical',
    'EMountainBikeRide',
    'GravelRide',
    'Handcycle',
    'MountainBikeRide',
    'Ride',
    'Velomobile',
    'VirtualRide',
  ],
  WALKING: ['Hike', 'Snowshoe', 'Wheelchair', 'Walk'],
  SWIMMING: ['Swim'],
};

export const StravaActivityGroupsTranslations: Record<
  StravaTriggerIntegrationActivityGroupsType,
  LocalizedString
> = {
  RUNNING: {en: 'Running', fr: 'Course à pied', es: 'Correr', de: 'Laufen'},
  CYCLING: {en: 'Cycling', fr: 'Cyclisme', es: 'Ciclismo', de: 'Radfahren'},
  WALKING: {en: 'Walking', fr: 'Marche à pied', es: 'Caminata', de: 'Gehen'},
  SWIMMING: {en: 'Swimming', fr: 'Natation', es: 'Natación', de: 'Schwimmen'},
};

// Translations taken from the official Strava website
export const StravaActivityTypesTranslations: Record<StravaActivityTypesType, LocalizedString> = {
  AlpineSki: {en: 'Alpine Ski', fr: 'Ski alpin', es: 'Esquí alpino', de: 'Ski Alpin'},
  BackcountrySki: {
    en: 'Backcountry Ski',
    fr: 'Ski de randonnée',
    es: 'Esquí de montaña',
    de: 'Tourenskilauf',
  },
  Badminton: {en: 'Badminton', fr: 'Badminton', es: 'Bádminton', de: 'Badminton'},
  Canoeing: {en: 'Canoe', fr: 'Canoë', es: 'Piragüismo', de: 'Kanu'},
  Crossfit: {en: 'Crossfit', fr: 'Crossfit', es: 'Crossfit', de: 'Crossfit'},
  EBikeRide: {
    en: 'E-Bike Ride',
    fr: 'Vélo électrique',
    es: 'Bicicleta eléctrica',
    de: 'E-Bike-Radfahrt',
  },
  EMountainBikeRide: {
    en: 'E-Mountain Bike Ride',
    fr: 'Sortie en VTT électrique',
    es: 'Bicicleta de montaña eléctrica',
    de: 'E-Mountainbike-Fahrt',
  },
  Elliptical: {en: 'Elliptical', fr: 'Elliptique', es: 'Elíptica', de: 'Elliptisch'},
  Golf: {en: 'Golf', fr: 'Golf', es: 'Golf', de: 'Golf'},
  GravelRide: {en: 'Gravel Ride', fr: 'Sortie en gravel', es: 'Gravel', de: 'Gravel-Fahrt'},
  Handcycle: {en: 'Handcycle', fr: 'Handbike', es: 'Handbike', de: 'Handbike-Fahrt'},
  HighIntensityIntervalTraining: {en: 'HIIT', fr: 'HIIT', es: 'HIIT', de: 'HIIT'},
  Hike: {en: 'Hike', fr: 'Randonnée', es: 'Senderismo', de: 'Wandern'},
  IceSkate: {en: 'Ice Skate', fr: 'Patin à glace', es: 'Patinaje sobre hielo', de: 'Eislaufen'},
  InlineSkate: {en: 'Inline Skate', fr: 'Roller', es: 'Patinaje en línea', de: 'Inline-Skaten'},
  Kayaking: {en: 'Kayaking', fr: 'Kayak', es: 'Kayak', de: 'Kajakfahrt'},
  Kitesurf: {en: 'Kitesurf', fr: 'Kitesurf', es: 'Kitesurf', de: 'Kitesurfen'},
  MountainBikeRide: {
    en: 'Mountain Bike Ride',
    fr: 'Sortie en VTT',
    es: 'Paseo en bicicleta de montaña',
    de: 'Mountainbike-Fahrt',
  },
  NordicSki: {en: 'Nordic Ski', fr: 'Ski nordique', es: 'Esquí nórdico', de: 'Ski Nordisch'},
  Pickleball: {en: 'Pickleball', fr: 'Pickleball', es: 'Pickleball', de: 'Pickleball'},
  Pilates: {en: 'Pilates', fr: 'Pilates', es: 'Pilates', de: 'Pilates'},
  Racquetball: {en: 'Racquetball', fr: 'Racquetball', es: 'Racquetball', de: 'Racquetball'},
  Ride: {en: 'Ride', fr: 'Vélo', es: 'Paseo', de: 'Radfahrt'},
  RockClimbing: {en: 'Rock Climb', fr: 'Escalade', es: 'Escalada en roca', de: 'Klettern'},
  RollerSki: {en: 'Roller Ski', fr: 'Ski à roulettes', es: 'Esquí sobre ruedas', de: 'Rollski'},
  Rowing: {en: 'Rowing', fr: 'Aviron', es: 'Remo', de: 'Rudern'},
  Run: {en: 'Run', fr: 'Course à pied', es: 'Correr', de: 'Lauf'},
  Sail: {en: 'Sail', fr: 'Voile', es: 'Navegar', de: 'Segeln'},
  Skateboard: {en: 'Skateboard', fr: 'Skateboard', es: 'Monopatín', de: 'Skateboarden'},
  Snowboard: {en: 'Snowboard', fr: 'Snowboard', es: 'Snowboard', de: 'Snowboarden'},
  Snowshoe: {en: 'Snowshoe', fr: 'Raquettes', es: 'Raquetas de nieve', de: 'Schneeschuhwanderung'},
  Soccer: {en: 'Football (Soccer)', fr: 'Football', es: 'Fútbol', de: 'Fußball'},
  Squash: {en: 'Squash', fr: 'Squash', es: 'Squash', de: 'Squash'},
  StairStepper: {
    en: 'Stair-Stepper',
    fr: "Simulateur d'escalier",
    es: 'Simulador de escaleras',
    de: 'Stufen-Stepper',
  },
  StandUpPaddling: {
    en: 'Stand Up Paddling',
    fr: 'Stand up paddle',
    es: 'Paddle surf',
    de: 'Stand Up Paddling',
  },
  Surfing: {en: 'Surfing', fr: 'Surf', es: 'Surf', de: 'Surfen'},
  Swim: {en: 'Swim', fr: 'Natation', es: 'Nadar', de: 'Schwimmen'},
  TableTennis: {en: 'Table Tennis', fr: 'Tennis de table', es: 'Tenis de mesa', de: 'Tischtennis'},
  Tennis: {en: 'Tennis', fr: 'Tennis', es: 'Tenis', de: 'Tennis'},
  TrailRun: {en: 'Trail Run', fr: 'Course en sentier', es: 'Carrera de sendero', de: 'Traillauf'},
  Velomobile: {en: 'Velomobile', fr: 'Vélomobile', es: 'Velomóvil', de: 'Velomobil'},
  VirtualRide: {
    en: 'Virtual Ride',
    fr: 'Sortie virtuelle',
    es: 'Paseo virtual',
    de: 'Virtuelle Radfahrt',
  },
  VirtualRow: {
    en: 'Virtual Row',
    fr: 'Rameur virtuel',
    es: 'Remo virtual',
    de: 'Virtuelles Rudern',
  },
  VirtualRun: {
    en: 'Virtual Run',
    fr: 'Course virtuelle',
    es: 'Carrera virtual',
    de: 'Virtueller Lauf',
  },
  Walk: {en: 'Walk', fr: 'Marche', es: 'Caminar', de: 'Spaziergang'},
  WeightTraining: {
    en: 'Weight Training',
    fr: 'Entraînement aux poids',
    es: 'Entrenamiento de pesas',
    de: 'Gewichtstraining',
  },
  Wheelchair: {
    en: 'Wheelchair',
    fr: 'Course en fauteuil',
    es: 'Silla de ruedas',
    de: 'Rollstuhlfahrt',
  },
  Windsurf: {en: 'Windsurf', fr: 'Windsurf', es: 'Windsurf', de: 'Windsurfen'},
  Workout: {en: 'Workout', fr: 'Entraînement', es: 'Entrenamiento', de: 'Training'},
  Yoga: {en: 'Yoga', fr: 'Yoga', es: 'Yoga', de: 'Yoga'},
};

export const StravaActivityGroupsSchema = z.enum(StravaTriggerIntegrationActivityGroups);
export type StravaActivityGroupsType = z.infer<typeof StravaActivityGroupsSchema>;

export const StravaActivityTypesSchema = z.enum(StravaActivityTypes);
export type StravaActivityTypesType = z.infer<typeof StravaActivityTypesSchema>;

export const StravaActivitySchema = z.object({
  id: z.number(),
  stravaUserId: z.number(),
  averageSpeedMetersPerSecond: z.number(),
  distanceMeters: z.number(),
  durationSeconds: z.number(),
  name: z.string(),
  startDate: DatetimeSchema,
  totalElevationGainInMeters: z.number(),
  type: StravaActivityTypesSchema,
});
export type StravaActivity = z.infer<typeof StravaActivitySchema>;

export function getStravaActivityGroupLabel(
  activityGroup: StravaActivityGroupsType,
  language: Language
): string {
  return (
    StravaActivityGroupsTranslations[activityGroup][language] ??
    StravaActivityGroupsTranslations[activityGroup].en ??
    activityGroup
  );
}
export function getStravaActivityTypeLabel(
  activityType: StravaActivityTypesType,
  language: Language
): string {
  return (
    StravaActivityTypesTranslations[activityType][language] ??
    StravaActivityTypesTranslations[activityType].en ??
    activityType
  );
}
